import React, { useState } from "react";
import {
  PERFORMANCE_COACH_COLLABORATORS_LABEL,
  PERFORMANCE_COACH_LABEL,
  CC_LABEL,
  COLLABORATOR_LABEL,
  COLLABORATORS_LABEL,
  COMPANY_DATA,
  EMAIL_LABEL,
  EMPTY_COLLABORATORS_MESSAGE,
  FISCAL_CODE_LABEL,
  INTERNAL_GRADE_LABEL,
  NAME_LABEL,
  NAME_SURNAME_EMAIL_LABEL,
  PERSONAL_DATA,
  PRACTICE_LABEL,
  SURNAME_LABEL,
} from "../../constants/Dictionary";
import { AlignmentEnum, FontWeightEnum, PageNames } from "../../types/AppTypes";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RoundedContainer } from "../../components/RoundedContainer/RoundedContainer";
import { FormSection } from "../../components/FormSection/FormSection";
import { ReactComponent as UserIcon } from "../../icons/userBox.svg";
import { ReactComponent as CompanyIcon } from "../../icons/company.svg";
import { ReactComponent as UsersIcon } from "../../icons/users.svg";
import { Divider } from "../../components/core/Divider/Divider";
import { StyledCareerManagerDetail } from "./CareerManagerDetail.styled";
import { DoubleStructure } from "../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../components/Typography/Typography";
import SearchInput from "../../components/core/SearchInput/SearchInput";
import { Loader } from "../../components/core/Loader/Loader";
import { getCmFollowedTableColumns } from "../../constants/TableColumns";
import {
  CAREER_MANAGERS_FOLLOWED_PAGE_SIZE,
  CollaboratorTypeEnum,
} from "../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import { TextBoxGrid } from "../../components/TextBoxGrid/TextBoxGrid.component";
import { Table, TableThemeEnum } from "../../components/core/Table/Table";
import { findCCById, findPracticeById } from "../../utils/orgFunctions.utils";
import { useAppSelector } from "../../hooks/store.hook";
import { useBreadCrumb } from "../../hooks/useBreadCrumbs.hooks";
import {
  DETAIL_QUERY_PARAM_KEY,
  decodeParams,
  encodeParams,
} from "../../utils/queryParams.utils";
import { Collaborator } from "../../types/api/collaborators.type";
import {
  useCareerManager,
  useCareerManagersFollowed,
} from "../../hooks/careerManagerDetail.hook";

export const CareerManagerDetail = () => {
  const navigate = useNavigate();
  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const queryParams = decodeParams(searchParams.get(DETAIL_QUERY_PARAM_KEY));
  const cmName = queryParams.careerManagerName ?? null;
  const cmSurname = queryParams.careerManagerSurname ?? null;
  const { careerManagerId } = useParams();

  const { data: detailsData, isFetching: fetchingDetails } = useCareerManager({
    careerManagerId,
  });

  const { data: followedData, isFetching: loadingFollowed } =
    useCareerManagersFollowed({
      careerManagerId,
      pageNumber,
      searchDebouncedValue,
    });

  useBreadCrumb([
    { name: PERFORMANCE_COACH_LABEL, pathName: PageNames.CAREER_MANAGER },
    {
      name: `${cmSurname} ${cmName}`,
      pathName: null,
    },
  ]);

  return (
    <RoundedContainer fullHeight>
      <StyledCareerManagerDetail>
        <Loader loading={fetchingDetails}>
          <div>
            <FormSection label={PERSONAL_DATA} icon={<UserIcon />}>
              <TextBoxGrid
                numCol={4}
                items={[
                  {
                    label: NAME_LABEL,
                    text: detailsData?.name,
                  },
                  {
                    label: SURNAME_LABEL,
                    text: detailsData?.surname,
                  },
                  {
                    label: FISCAL_CODE_LABEL,
                    text: detailsData?.taxIdCode,
                  },
                  {
                    label: EMAIL_LABEL,
                    text: detailsData?.email,
                  },
                ]}
              />
            </FormSection>
            <Divider />
            <FormSection label={COMPANY_DATA} icon={<CompanyIcon />}>
              <TextBoxGrid
                numCol={4}
                items={[
                  {
                    label: PRACTICE_LABEL,
                    text: detailsData?.practice
                      ? findPracticeById(
                          orgSettings.areaList.all,
                          detailsData?.practice,
                        )?.name
                      : null,
                  },
                  {
                    label: CC_LABEL,
                    text: detailsData?.cc
                      ? findCCById(orgSettings.areaList.all, detailsData?.cc)
                          ?.name
                      : null,
                  },
                  {
                    label: INTERNAL_GRADE_LABEL,
                    text: detailsData?.internalGrade,
                  },
                ]}
              />
            </FormSection>
            <Divider />
          </div>
        </Loader>
        <FormSection
          label={PERFORMANCE_COACH_COLLABORATORS_LABEL}
          icon={<UsersIcon />}
        >
          <div className="table-header">
            <DoubleStructure
              verticalAlignment={AlignmentEnum.CENTER}
              variant="primary"
            >
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {`${followedData?.pagination.total ?? 0} ${followedData?.pagination.total == 1 ? COLLABORATOR_LABEL : COLLABORATORS_LABEL}`}
              </BodyTyp>
              <div className="search-container">
                <SearchInput
                  value={searchValue}
                  onChange={setSearchValue}
                  label={NAME_SURNAME_EMAIL_LABEL}
                  name={"search"}
                />
              </div>
            </DoubleStructure>
          </div>
          <Table<Collaborator>
            theme={TableThemeEnum.DARK}
            loading={loadingFollowed}
            headers={getCmFollowedTableColumns(
              CollaboratorTypeEnum.FOLLOW,
              orgSettings.areaList.all,
              orgSettings.cmsheet,
            )}
            data={followedData ? followedData.data : []}
            clickableRow={(record) =>
              record.hasDetailsVisibility &&
              !profile.restrictedCollaboratorIds.includes(record.id)
            }
            scrollY={"calc(100dvh - 496px)"}
            onRow={(record) => {
              navigate(
                `${PageNames.CAREER_MANAGER}/${careerManagerId}/collaborators/${record.id}?${DETAIL_QUERY_PARAM_KEY}=${encodeParams(
                  {
                    collaboratorName: record.name,
                    collaboratorSurname: record.surname,
                    careerManagerName: cmName ?? "",
                    careerManagerSurname: cmSurname ?? "",
                  },
                )}`,
              );
            }}
            pagination={{
              current: pageNumber + 1,
              onChange: (pageNumber) => setPageNumber(pageNumber - 1),
              total: followedData?.pagination.total || 0,
              pageSize: CAREER_MANAGERS_FOLLOWED_PAGE_SIZE,
            }}
            emptyText={loadingFollowed ? "" : EMPTY_COLLABORATORS_MESSAGE}
          />
        </FormSection>
      </StyledCareerManagerDetail>
    </RoundedContainer>
  );
};
